import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import RoomIcon from '@mui/icons-material/Room';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL, ru as ruRU } from 'date-fns/locale';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import {
  HostMonthlyEarningsResponse,
  MonthlyEarningsTotal,
  MonthlyRoomEarnings,
  MonthlySpaceEarnings,
  MonthlyTutorStatistics,
} from '../../models/Earnings';

const MONTH_FORMAT = 'yyyy-MM';
const TREND_MONTHS = 6;
const TREND_BAR_COLOR = '#6366f1';

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const resolveDateFnsLocale = (language?: string): Locale => {
  if (language?.startsWith('pl')) return plPL;
  if (language?.startsWith('ru')) return ruRU;
  return enGB;
};

export type HostEarningsReportType = 'rehearsals' | 'tutors';

interface HostMonthlyEarningsPageProps {
  reportType?: HostEarningsReportType;
}

const mergeTotals = (groups: Array<{ totals: MonthlyEarningsTotal[] }>): MonthlyEarningsTotal[] => {
  const totalsByCurrency = new Map<string, number>();
  groups.forEach((group) =>
    group.totals.forEach((total) =>
      totalsByCurrency.set(
        total.currencyCode,
        (totalsByCurrency.get(total.currencyCode) ?? 0) + total.totalAmount,
      ),
    ),
  );
  return Array.from(totalsByCurrency, ([currencyCode, totalAmount]) => ({
    currencyCode,
    totalAmount,
  })).sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
};

const getReportTotals = (
  response: HostMonthlyEarningsResponse,
  reportType: HostEarningsReportType,
): MonthlyEarningsTotal[] =>
  mergeTotals(reportType === 'tutors' ? response.tutors ?? [] : response.spaces ?? []);

const HostMonthlyEarningsPage: React.FC<HostMonthlyEarningsPageProps> = ({
  reportType = 'rehearsals',
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTutorReport = reportType === 'tutors';

  const [selectedMonth, setSelectedMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [data, setData] = useState<HostMonthlyEarningsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [trendRawByMonth, setTrendRawByMonth] = useState<Map<string, HostMonthlyEarningsResponse>>(
    () => new Map(),
  );
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const monthString = useMemo(() => format(selectedMonth, MONTH_FORMAT), [selectedMonth]);

  const adapterLocale = useMemo(() => resolveDateFnsLocale(i18n.language), [i18n.language]);

  /** Last `TREND_MONTHS` calendar months (inclusive of the selected month), oldest first. */
  const trendWindow = useMemo<Date[]>(() => {
    const end = startOfMonth(selectedMonth);
    const months: Date[] = [];
    for (let i = TREND_MONTHS - 1; i >= 0; i -= 1) {
      months.push(new Date(end.getFullYear(), end.getMonth() - i, 1));
    }
    return months;
  }, [selectedMonth]);

  const trendMonthStrings = useMemo(
    () => trendWindow.map((d) => format(d, MONTH_FORMAT)),
    [trendWindow],
  );

  /** One pass loads the selected month (detail) and the trend window — no duplicate month fetch. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTrendLoading(true);
    setError(null);
    setTrendError(null);

    Promise.allSettled(
      trendMonthStrings.map((month) =>
        hostApiClient.getHostMonthlyEarnings(month).then((response) => ({ month, response })),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        const next = new Map<string, HostMonthlyEarningsResponse>();
        let okCount = 0;
        let selected: HostMonthlyEarningsResponse | null = null;
        let selectedFailed = false;

        results.forEach((r, index) => {
          const month = trendMonthStrings[index];
          if (r.status === 'fulfilled') {
            okCount += 1;
            next.set(month, r.value.response);
            if (month === monthString) {
              selected = r.value.response;
            }
          } else if (month === monthString) {
            selectedFailed = true;
            const err: any = r.reason;
            const status = err?.response?.status;
            if (status === 400) {
              setError(
                t(
                  'hostEarnings.errorInvalidMonth',
                  'Invalid month. Please pick a valid month and try again.',
                ),
              );
            } else {
              setError(
                err?.response?.data?.message ||
                  t('hostEarnings.fetchError', 'Failed to load earnings. Please try again.'),
              );
            }
          }
        });

        setTrendRawByMonth(next);
        setData(selected);
        if (selectedFailed && !selected) {
          setData(null);
        }
        if (okCount === 0) {
          setTrendError(t('hostEarnings.trendError', 'Could not load earnings history.'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setTrendLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [trendMonthStrings, monthString, t]);

  const trendByMonth = useMemo(() => {
    const next = new Map<string, MonthlyEarningsTotal[]>();
    trendRawByMonth.forEach((response, month) => {
      next.set(month, getReportTotals(response, reportType));
    });
    return next;
  }, [trendRawByMonth, reportType]);

  const formatAmount = useCallback(
    (amount: number, currencyCode: string): string => {
      try {
        return new Intl.NumberFormat(i18n.language || 'en', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return `${currencyCode} ${amount.toFixed(2)}`;
      }
    },
    [i18n.language],
  );

  /** Locale-aware month label, e.g. "May 2026" / "maj 2026". */
  const formatMonthLabel = useCallback(
    (date: Date, style: 'short' | 'long' = 'short'): string => {
      try {
        return new Intl.DateTimeFormat(i18n.language || 'en', {
          month: style,
          year: 'numeric',
        }).format(date);
      } catch {
        return format(date, 'MMM yyyy');
      }
    },
    [i18n.language],
  );

  const trendChartLabels = useMemo(
    () => trendWindow.map((d) => formatMonthLabel(d)),
    [trendWindow, formatMonthLabel],
  );

  const trendCurrencies = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(trendByMonth.values()).flatMap((totals) =>
            totals.map((total) => total.currencyCode),
          ),
        ),
      ).sort(),
    [trendByMonth],
  );

  const trendSeries = useMemo(
    () =>
      trendCurrencies.map((currencyCode, index) => ({
        data: trendMonthStrings.map(
          (month) =>
            trendByMonth
              .get(month)
              ?.find((total) => total.currencyCode === currencyCode)?.totalAmount ?? 0,
        ),
        label: `${t('hostEarnings.trendSeriesLabel', 'Earnings')} (${currencyCode})`,
        color: index === 0 ? TREND_BAR_COLOR : theme.palette.secondary.main,
        valueFormatter: (value: number | null) =>
          value == null ? '' : formatAmount(value, currencyCode),
      })),
    [trendCurrencies, trendMonthStrings, trendByMonth, formatAmount, t, theme.palette.secondary.main],
  );

  const hasTrendData = trendSeries.some((series) => series.data.some((value) => value > 0));

  const reportTotals = useMemo(
    () => (data ? getReportTotals(data, reportType) : []),
    [data, reportType],
  );

  const reportBookingCount = useMemo(
    () =>
      isTutorReport
        ? (data?.tutors ?? []).reduce((sum, tutor) => sum + tutor.bookingCount, 0)
        : (data?.spaces ?? []).reduce((sum, space) => sum + space.bookingCount, 0),
    [data, isTutorReport],
  );

  const selectedMonthLabel = useMemo(
    () => formatMonthLabel(selectedMonth, 'long'),
    [selectedMonth, formatMonthLabel],
  );

  const renderTotals = (
    totals: MonthlyEarningsTotal[],
    options: { dense?: boolean; align?: 'start' | 'end' } = {},
  ) => {
    const { dense = false, align = 'start' } = options;
    if (totals.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('hostEarnings.emptyState', 'No earnings for this month yet')}
        </Typography>
      );
    }
    return (
      <Stack
        direction="column"
        spacing={dense ? 0.25 : 0.5}
        alignItems={align === 'end' ? 'flex-end' : 'flex-start'}
      >
        {totals.map((total) => (
          <Typography
            key={`${total.currencyCode}-${total.totalAmount}`}
            variant={dense ? 'body2' : 'h6'}
            fontWeight={dense ? 500 : 700}
          >
            {formatAmount(total.totalAmount, total.currencyCode)}
          </Typography>
        ))}
      </Stack>
    );
  };

  const renderMetric = (label: string, content: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'block', md: 'none' }, textTransform: 'uppercase', mb: 0.25 }}
      >
        {label}
      </Typography>
      {content}
    </Box>
  );

  const renderRoom = (room: MonthlyRoomEarnings) => (
    <Box
      key={room.roomId}
      sx={{
        display: { xs: 'flex', md: 'grid' },
        flexDirection: 'column',
        gridTemplateColumns: 'minmax(180px, 1fr) 72px minmax(110px, 0.55fr) minmax(110px, 0.55fr)',
        gap: { xs: 1, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.common.white, 0.035),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <RoomIcon fontSize="small" color="action" />
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
          {room.roomName}
        </Typography>
      </Box>
      {renderMetric(
        t('hostEarnings.rehearsalsColumn', 'Rehearsals'),
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{room.bookingCount}</Typography>,
      )}
      {renderMetric(
        t('hostEarnings.revenueColumn', 'Revenue'),
        <Box sx={{ color: 'success.main' }}>{renderTotals(room.totals, { dense: true })}</Box>,
      )}
      {renderMetric(
        t('hostEarnings.averageColumn', 'Avg./booking'),
        <Stack spacing={0.25}>
          {room.totals.map((total) => (
            <Typography key={total.currencyCode} variant="body2" sx={{ fontWeight: 600 }}>
              {formatAmount(total.totalAmount / Math.max(room.bookingCount, 1), total.currencyCode)}
            </Typography>
          ))}
        </Stack>,
      )}
    </Box>
  );

  const renderSpace = (space: MonthlySpaceEarnings) => (
    <Box
      key={space.spaceId}
      sx={{
        display: { xs: 'flex', md: 'grid' },
        flexDirection: 'column',
        gridTemplateColumns: 'minmax(220px, 1fr) 72px minmax(110px, 0.55fr) minmax(110px, 0.55fr)',
        gap: { xs: 1.5, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.common.white, 0.04),
        border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
        <HomeIcon color="primary" />
        <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>{space.spaceName}</Typography>
      </Box>
      {renderMetric(
        t('hostEarnings.rehearsalsColumn', 'Rehearsals'),
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{space.bookingCount}</Typography>,
      )}
      {renderMetric(
        t('hostEarnings.revenueColumn', 'Revenue'),
        <Box sx={{ color: 'success.main' }}>{renderTotals(space.totals, { dense: true })}</Box>,
      )}
      {renderMetric(
        t('hostEarnings.averageColumn', 'Avg./booking'),
        <Stack spacing={0.25}>
          {space.totals.map((total) => (
            <Typography key={total.currencyCode} variant="body2" sx={{ fontWeight: 600 }}>
              {formatAmount(total.totalAmount / Math.max(space.bookingCount, 1), total.currencyCode)}
            </Typography>
          ))}
        </Stack>,
      )}
      {space.rooms.length > 0 && (
        <Box sx={{ gridColumn: '1 / -1', pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>
            {t('hostEarnings.roomsTitle', 'Rooms')}
          </Typography>
          <Stack spacing={1}>{space.rooms.map(renderRoom)}</Stack>
        </Box>
      )}
    </Box>
  );

  const tutors = data?.tutors ?? [];
  const spaces = data?.spaces ?? [];
  const isEmpty = !!data && (isTutorReport ? tutors.length === 0 : spaces.length === 0);

  const tutorInitials = (name: string): string =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?';

  const renderTutorRow = (tutor: MonthlyTutorStatistics) => (
    <Box
      key={tutor.tutorId}
      sx={{
        display: { xs: 'flex', md: 'grid' },
        flexDirection: 'column',
        gridTemplateColumns: 'minmax(220px, 1fr) 72px minmax(110px, 0.55fr) minmax(110px, 0.55fr)',
        gap: { xs: 1.5, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        p: 2,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.common.white, 0.04),
        border: `1px solid ${alpha(theme.palette.common.white, 0.04)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
        <Avatar
          src={tutor.tutorProfileImageUrl || undefined}
          alt={tutor.tutorName}
          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.85), fontWeight: 700 }}
        >
          {tutorInitials(tutor.tutorName)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
            {tutor.tutorName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('hostEarnings.studentCount', '{{count}} students', { count: tutor.studentCount })}
          </Typography>
        </Box>
      </Box>
      {renderMetric(
        t('hostEarnings.lessonsColumn', 'Lessons'),
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {tutor.bookingCount}
        </Typography>,
      )}
      {renderMetric(
        t('hostEarnings.revenueColumn', 'Revenue'),
        <Box sx={{ color: 'success.main' }}>{renderTotals(tutor.totals, { dense: true })}</Box>,
      )}
      {renderMetric(
        t('hostEarnings.averageColumn', 'Avg./lesson'),
        <Stack spacing={0.25}>
          {tutor.totals.map((total) => (
            <Typography key={total.currencyCode} variant="body2" sx={{ fontWeight: 600 }}>
              {formatAmount(total.totalAmount / Math.max(tutor.bookingCount, 1), total.currencyCode)}
            </Typography>
          ))}
        </Stack>,
      )}
      {tutor.lessonTypes.length > 0 && (
        <Box
          sx={{
            gridColumn: '1 / -1',
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 700, mb: 1 }}
          >
            {t('hostEarnings.lessonTypesTitle', 'Lesson types')}
          </Typography>
          <Stack spacing={1}>
            {tutor.lessonTypes.map((lesson) => (
              <Box
                key={`${lesson.lessonOptionId ?? lesson.lessonOptionName}-${lesson.sessionType}-${lesson.durationMinutes}`}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.common.white, 0.035),
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {lesson.lessonOptionName || t('hostEarnings.unknownLesson', 'Lesson')}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        lesson.sessionType === 'ONLINE'
                          ? t('hostEarnings.sessionOnline', 'Online')
                          : t('hostEarnings.sessionOffline', 'On-site')
                      }
                    />
                    {lesson.durationMinutes != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('hostEarnings.durationMinutes', '{{count}} min', {
                          count: lesson.durationMinutes,
                        })}
                      />
                    )}
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={t('hostEarnings.bookingCount', '{{count}} bookings', {
                        count: lesson.bookingCount,
                      })}
                    />
                  </Stack>
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  {renderTotals(lesson.totals, { dense: true, align: 'end' })}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );

  const escapeCsv = (value: string | number): string => {
    const normalized = String(value);
    return /[",\n]/.test(normalized) ? `"${normalized.replace(/"/g, '""')}"` : normalized;
  };

  const exportCsv = () => {
    if (!data) return;
    const rows: Array<Array<string | number>> = [];
    if (isTutorReport) {
      rows.push([
        t('hostEarnings.csv.tutor', 'Tutor'),
        t('hostEarnings.csv.lessons', 'Lessons'),
        t('hostEarnings.csv.students', 'Students'),
        t('hostEarnings.csv.currency', 'Currency'),
        t('hostEarnings.csv.revenue', 'Revenue'),
        t('hostEarnings.csv.averagePerLesson', 'Average per lesson'),
        t('hostEarnings.csv.lessonDetails', 'Lesson details'),
      ]);
      tutors.forEach((tutor) => {
        const lessonDetails = tutor.lessonTypes
          .map((lesson) =>
            [
              lesson.lessonOptionName || t('hostEarnings.unknownLesson', 'Lesson'),
              lesson.sessionType === 'ONLINE'
                ? t('hostEarnings.sessionOnline', 'Online')
                : t('hostEarnings.sessionOffline', 'On-site'),
              lesson.durationMinutes == null
                ? ''
                : t('hostEarnings.durationMinutes', '{{count}} min', {
                    count: lesson.durationMinutes,
                  }),
              t('hostEarnings.bookingCount', '{{count}} bookings', {
                count: lesson.bookingCount,
              }),
              lesson.totals.map((total) => `${total.totalAmount} ${total.currencyCode}`).join(' / '),
            ]
              .filter(Boolean)
              .join(' · '),
          )
          .join(' | ');
        const totals = tutor.totals.length > 0 ? tutor.totals : [{ currencyCode: '', totalAmount: 0 }];
        totals.forEach((total) => {
          rows.push([
            tutor.tutorName,
            tutor.bookingCount,
            tutor.studentCount,
            total.currencyCode,
            total.totalAmount,
            total.totalAmount / Math.max(tutor.bookingCount, 1),
            lessonDetails,
          ]);
        });
      });
    } else {
      rows.push([
        t('hostEarnings.csv.space', 'Space'),
        t('hostEarnings.csv.room', 'Room'),
        t('hostEarnings.csv.rehearsals', 'Rehearsals'),
        t('hostEarnings.csv.currency', 'Currency'),
        t('hostEarnings.csv.revenue', 'Revenue'),
        t('hostEarnings.csv.averagePerRehearsal', 'Average per rehearsal'),
      ]);
      spaces.forEach((space) => {
        space.rooms.forEach((room) => {
          const totals = room.totals.length > 0 ? room.totals : [{ currencyCode: '', totalAmount: 0 }];
          totals.forEach((total) => {
            rows.push([
              space.spaceName,
              room.roomName,
              room.bookingCount,
              total.currencyCode,
              total.totalAmount,
              total.totalAmount / Math.max(room.bookingCount, 1),
            ]);
          });
        });
      });
    }
    const csv = rows.map((row) => row.map(escapeCsv).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${isTutorReport ? 'tutor-statistics' : 'rehearsal-earnings'}-${monthString}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1440, width: '100%', mx: 'auto' }}>
      <Card
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: alpha(theme.palette.common.white, 0.035),
          border: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {isTutorReport
                ? t('hostEarnings.pageTitle', 'Tutor statistics')
                : t('hostRehearsalEarnings.pageTitle', 'Rehearsal earnings')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {isTutorReport
                ? t(
                    'hostEarnings.helperText',
                    'Only completed, confirmed tutor lessons in your organization are included.',
                  )
                : t(
                    'hostRehearsalEarnings.helperText',
                    'Only completed, confirmed rehearsal bookings in your organization are included.',
                  )}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
              <DatePicker
                label={t('hostEarnings.monthLabel', 'Month')}
                views={['year', 'month']}
                openTo="month"
                value={selectedMonth}
                onChange={(value) => {
                  if (value && !Number.isNaN(value.getTime())) {
                    setSelectedMonth(startOfMonth(value));
                  }
                }}
                slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 210 } } } }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!data || isEmpty}
              onClick={exportCsv}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('hostEarnings.exportCsv', 'Export CSV')}
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && data && (
          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Card
                variant="outlined"
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(
                    theme.palette.primary.main,
                    0.035,
                  )})`,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {isTutorReport
                        ? t('hostEarnings.lessonsThisMonthTitle', 'Lessons this month')
                        : t('hostRehearsalEarnings.rehearsalsThisMonthTitle', 'Rehearsals this month')}
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
                      {reportBookingCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {isTutorReport
                        ? t('hostEarnings.lessonsThisMonthCaption', 'Confirmed across all tutors')
                        : t('hostRehearsalEarnings.rehearsalsThisMonthCaption', 'Confirmed across all spaces')}
                    </Typography>
                  </Box>
                  {isTutorReport ? (
                    <SchoolIcon sx={{ color: 'primary.light', fontSize: 36 }} />
                  ) : (
                    <RoomIcon sx={{ color: 'primary.light', fontSize: 36 }} />
                  )}
                </Box>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  p: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)}, ${alpha(
                    theme.palette.success.main,
                    0.035,
                  )})`,
                  borderColor: alpha(theme.palette.success.main, 0.3),
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {isTutorReport
                        ? t('hostEarnings.tutorRevenueTitle', 'Tutor revenue')
                        : t('hostRehearsalEarnings.revenueTitle', 'Rehearsal revenue')}
                    </Typography>
                    <Box sx={{ mt: 1 }}>{renderTotals(reportTotals)}</Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {selectedMonthLabel}
                    </Typography>
                  </Box>
                  <AccountBalanceIcon sx={{ color: 'success.light', fontSize: 36 }} />
                </Box>
              </Card>
            </Box>

            {isEmpty && (
              <Alert severity="info">{t('hostEarnings.emptyState', 'No earnings for this month yet')}</Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.3fr) minmax(360px, 0.8fr)' },
                gap: 3,
                alignItems: 'start',
              }}
            >
              <Stack spacing={3} sx={{ minWidth: 0 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {isTutorReport
                      ? t('hostEarnings.tutorTableTitle', 'Tutor performance')
                      : t('hostRehearsalEarnings.spaceTableTitle', 'Space performance')}
                  </Typography>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'grid' },
                      gridTemplateColumns: 'minmax(220px, 1fr) 72px minmax(110px, 0.55fr) minmax(110px, 0.55fr)',
                      gap: 2,
                      px: 2,
                      pb: 1,
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="overline">
                      {isTutorReport
                        ? t('hostEarnings.tutorColumn', 'Tutor')
                        : t('hostRehearsalEarnings.spaceColumn', 'Space')}
                    </Typography>
                    <Typography variant="overline">
                      {isTutorReport
                        ? t('hostEarnings.lessonsColumn', 'Lessons')
                        : t('hostEarnings.rehearsalsColumn', 'Rehearsals')}
                    </Typography>
                    <Typography variant="overline">{t('hostEarnings.revenueColumn', 'Revenue')}</Typography>
                    <Typography variant="overline">
                      {isTutorReport
                        ? t('hostEarnings.averageColumn', 'Avg./lesson')
                        : t('hostRehearsalEarnings.averageColumn', 'Avg./rehearsal')}
                    </Typography>
                  </Box>
                  {(isTutorReport ? tutors.length : spaces.length) > 0 ? (
                    <Stack spacing={1}>
                      {isTutorReport ? tutors.map(renderTutorRow) : spaces.map(renderSpace)}
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      {isTutorReport
                        ? t('hostEarnings.tutorsEmpty', 'No completed tutor lessons this month.')
                        : t('hostRehearsalEarnings.empty', 'No completed rehearsals this month.')}
                    </Alert>
                  )}
                </Box>

                {(isTutorReport ? tutors.length : spaces.length) > 0 && (
                  <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: 'transparent' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {isTutorReport
                        ? t('hostEarnings.comparisonTitle', 'Lesson comparison')
                        : t('hostRehearsalEarnings.comparisonTitle', 'Rehearsal comparison')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isTutorReport
                        ? t('hostEarnings.comparisonSubtitle', 'Completed lessons by tutor in {{month}}', {
                            month: selectedMonthLabel,
                          })
                        : t(
                            'hostRehearsalEarnings.comparisonSubtitle',
                            'Completed rehearsals by space in {{month}}',
                            { month: selectedMonthLabel },
                          )}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        overflowX: 'auto',
                        '& .MuiBarElement-root': { rx: 6, ry: 6 },
                        '& .MuiChartsAxis-tickLabel': { fontSize: '0.75rem' },
                      }}
                    >
                      <BarChart
                        xAxis={[
                          {
                            scaleType: 'band' as const,
                            data: isTutorReport
                              ? tutors.map((tutor) => tutorInitials(tutor.tutorName))
                              : spaces.map((space) => space.spaceName),
                          },
                        ]}
                        series={[
                          {
                            data: isTutorReport
                              ? tutors.map((tutor) => tutor.bookingCount)
                              : spaces.map((space) => space.bookingCount),
                            label: isTutorReport
                              ? t('hostEarnings.tutorSeriesLabel', 'Lessons')
                              : t('hostRehearsalEarnings.seriesLabel', 'Rehearsals'),
                            color: theme.palette.primary.main,
                          },
                        ]}
                        height={260}
                        margin={{ top: 24, right: 12, bottom: 36, left: 36 }}
                      />
                    </Box>
                  </Card>
                )}
              </Stack>

              <Card variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: 'transparent' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('hostEarnings.trendTitle', 'Trend — 6 months')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  {isTutorReport
                    ? t('hostEarnings.trendSubtitle', 'Tutor revenue from confirmed lessons.')
                    : t('hostRehearsalEarnings.trendSubtitle', 'Rehearsal revenue from confirmed bookings.')}
                </Typography>

                {trendLoading && (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={260}>
                    <CircularProgress />
                  </Box>
                )}
                {!trendLoading && trendError && <Alert severity="warning">{trendError}</Alert>}
                {!trendLoading && !trendError && !hasTrendData && (
                  <Alert severity="info">
                    {t('hostEarnings.trendEmpty', 'No earnings recorded in the last 6 months.')}
                  </Alert>
                )}
                {!trendLoading && !trendError && hasTrendData && (
                  <Box
                    sx={{
                      width: '100%',
                      minWidth: 0,
                      overflowX: 'auto',
                      '& .MuiChartsAxis-tickLabel': { fontSize: '0.7rem' },
                      '& .MuiChartsAxis-line': { stroke: theme.palette.divider },
                      '& .MuiBarElement-root': { rx: 6, ry: 6 },
                    }}
                  >
                    <BarChart
                      xAxis={[{ scaleType: 'band' as const, data: trendChartLabels }]}
                      series={trendSeries}
                      height={isMobile ? 280 : 330}
                      margin={{ top: 24, right: 12, bottom: 56, left: isMobile ? 48 : 64 }}
                    />
                  </Box>
                )}
              </Card>
            </Box>
          </Stack>
        )}
      </Card>
    </Box>
  );
};

export default HostMonthlyEarningsPage;
